import { createApp } from 'vue'

/* stylings */
// import '@fontsource/roboto'
import '@/assets/styles/index.css'
import '@/assets/styles/reset.css'

/* entity */
import { Currency } from '@workspace/sab-core/domain/model/basics'

/* Template commons */
import { createAppState, initializeAPIs } from '@workspace/template-commons/composables/config'
import globalBus from '@workspace/template-commons/plugins/globalBus'

/* translations */
import createI18n from './translations'

interface SearchMaskEntryParams {
  elasticIndices: Record<string, string>
  searchMaskOnly: boolean | undefined
}

async function mountHotelResults (
  i18n: Awaited<ReturnType<typeof createI18n>>,
  state: ReturnType<typeof createAppState>
) {
  const HotelResultsEntry = import('./components/hotelresults/HotelResultsEntry.vue')

  const mountSelector = 'br-hotel-results'
  const mountPoint = document.querySelector(mountSelector)

  if (!mountPoint) {
    throw new TypeError(`[Busyrooms Integration] Mount point '${mountSelector}' was not found`)
  }

  /* Create app */
  const app = createApp(
    (await HotelResultsEntry).default
  )

  app.use(globalBus)
  app.use(i18n)
  app.use(state)

  app.mount(mountPoint)
}

async function mountSearchMask (
  params: SearchMaskEntryParams,
  i18n: Awaited<ReturnType<typeof createI18n>>,
  state: ReturnType<typeof createAppState>
) {
  const SearchMaskEntry = import('./components/search/SearchMaskEntry.vue')

  const mountSelector = 'br-search-mask'
  const mountPoint = document.querySelector(mountSelector)

  if (!mountPoint) {
    throw new TypeError(`[Busyrooms Integration] Mount point '${mountSelector}' was not found`)
  }

  /* Create app */
  const app = createApp(
    (await SearchMaskEntry).default,
    params as unknown as Record<string, unknown>
  )

  app.use(globalBus)
  app.use(i18n)
  app.use(state)

  app.mount(mountPoint)
}

interface MountSABParams {
  lang: string
  currency?: Currency // defaults to CHF
  checkin?: string
  checkout?: string
  isStage?: boolean // defaults to false
  searchMaskOnly?: boolean
}

declare global {
  interface Window {
    BusyRooms: {
      mountSAB?: (params: MountSABParams) => void

      onLoaded?: () => void
    },
    loginStatus: string
  }
}

/* Create BusyRooms namespace if it doesn't exist */
if (typeof window.BusyRooms === 'undefined') {
  window.BusyRooms = {}
}

/* Add general function to window */
window.BusyRooms.mountSAB = async function (params) {
  const currency = params.currency || Currency.CHF
  const i18n = await createI18n(currency, params.lang)
  const isStage = params.isStage || false

  /* Initial dates */
  const checkin = checkAndGetDate(params.checkin)
  const checkout = checkAndGetDate(params.checkout)

  /* elasticsearch */
  const prefix = isStage ? 'cms_10004-stage_' : 'cms_10069_'
  const suffix = `_${params.lang}`
  const elasticIndices: Record<string, string> = {
    hotel: `${prefix}hotel${suffix}`,
    place: `${prefix}place${suffix}`,
    destination: `${prefix}destination${suffix}`,
    promotionCode: `${prefix}promotioncode${suffix}`,
    ratePlan: `${prefix}rateplan${suffix}`
  }

  /* Initialize config */
  const state = createAppState(
    {
      currency,
      promocode: '',
      checkin,
      checkout,
      isStage,
      elasticIndices
    },
    i18n.global.locale
  )

  initializeAPIs()

  if (!params.searchMaskOnly) {
    await mountHotelResults(i18n, state)
  }

  mountSearchMask({
    elasticIndices,
    searchMaskOnly: params.searchMaskOnly
  }, i18n, state)

  const buildTimestamp = process.env.BUILD_TIMESTAMP
  if (buildTimestamp) {
    console.log(`[BR-SAB] Build time: ${new Date(buildTimestamp).toString()}`)
  }
}

/* Check listener */
if (typeof window.BusyRooms.onLoaded === 'function') {
  window.BusyRooms.onLoaded()
}

function checkAndGetDate (v: string | undefined) {
  const result = new Date(v || '')
  return result instanceof Date && !isNaN(result.valueOf())
    ? result
    : undefined
}
